import {
  registerDecorator,
  type ValidationArguments,
  type ValidationOptions,
} from 'class-validator';
import { isValidPartitaIva, isValidVatNumberFormat, normalizeVatNumber } from './partita-iva';

/**
 * Validates a P.IVA (Italian VAT number): optional "IT" prefix and
 * spaces/dots/dashes are normalized away, then checked as 11 digits with a
 * valid Luhn-style checksum (see `isValidPartitaIva`).
 */
export function IsPartitaIva(validationOptions?: ValidationOptions) {
  return (object: object, propertyName: string) => {
    registerDecorator({
      name: 'isPartitaIva',
      target: object.constructor,
      propertyName,
      options: validationOptions,
      validator: {
        validate(value: unknown, _args: ValidationArguments) {
          return typeof value === 'string' && isValidPartitaIva(normalizeVatNumber(value));
        },
        defaultMessage() {
          return 'Partita IVA non valida';
        },
      },
    });
  };
}

/**
 * Validates the P.IVA length only, without the checksum: separators and an
 * optional "IT" prefix are normalized away, then an Italian VAT must be exactly
 * 11 digits (PRSP-135). A foreign VAT — recognized by its country-code prefix,
 * which suppliers legitimately have when transcribed from an import delivery
 * note — keeps its own national length.
 */
export function IsPartitaIvaFormat(validationOptions?: ValidationOptions) {
  return (object: object, propertyName: string) => {
    registerDecorator({
      name: 'isPartitaIvaFormat',
      target: object.constructor,
      propertyName,
      options: validationOptions,
      validator: {
        validate(value: unknown) {
          if (typeof value !== 'string') {
            return false;
          }
          return isValidVatNumberFormat(value);
        },
        defaultMessage() {
          return 'Inserisci 11 cifre per una Partita IVA italiana, oppure la sigla del Paese seguita dal numero (es. DE123456789).';
        },
      },
    });
  };
}
